import { Loader2, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.js';
import type { HandshakeState } from '@/handshake/useHandshake.js';

interface Props {
  handshake: HandshakeState;
  children: ReactNode;
}

export function HandshakeGate({ handshake, children }: Props) {
  if (handshake.status === 'connecting') {
    return <ConnectingState />;
  }

  if (handshake.status === 'error') {
    return <ErrorState message={handshake.error ?? 'Unknown handshake error.'} />;
  }

  return <>{children}</>;
}

function ConnectingState() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-3 bg-background p-8">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-background p-8">
      <Card className="w-full max-w-md border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" />
            Failed to connect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{message}</p>
          <p className="text-xs text-muted-foreground">
            This extension expects to run inside the Kadanza Admin Console iframe. When loaded
            standalone, configure{' '}
            <code className="rounded bg-muted px-1">VITE_OAUTH_ISSUER</code> +{' '}
            <code className="rounded bg-muted px-1">VITE_OAUTH_CLIENT_ID</code> for a real
            OAuth (auth code + PKCE) sign-in, or set{' '}
            <code className="rounded bg-muted px-1">VITE_DEV_JWT</code> to synthesise a handshake
            locally.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry handshake
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
